import ChatWorkerClient from './ChatWorkerClient';
import FileUtils from '../utils/FileUtils';
import { v4 as uuidV4 } from 'uuid';

import RealChatClient from '../chat/network/ChatClient';
import RealMessageHandler from '../chat/network/MessageHandler';
import RealAccountHandler from '../chat/network/AccountHandler';
import RealPushHandler from '../chat/network/PushHandler';
import RealPresenceHandler from '../chat/network/PresenceHandler';
import RealPinHandler from '../chat/network/PinHandler';
import RealS3Handler from '../chat/network/S3Handler';
import RealSMSHandler from '../chat/network/SMSHandler';
import RealCareHandler from '../chat/network/CareHandler';
import RealGroupHandler from '../chat/network/GroupHandler';
import RealInitHandler from '../chat/network/InitHandler';
import RealSuspectHandler from '../chat/network/SuspectHandler';

import RealChannelDBOps from '../chat/database/ChannelDBOps';
import RealUserDBOps from '../chat/database/UserDBOps';

const workerClient = ChatWorkerClient.shared;
const useWorker = workerClient.isWorkerMode();
if (!useWorker) {
  console.warn('[chat-proxy] using direct (non-worker) chat clients');
}

console.log('ChatWorkerClient mode: ', useWorker ? 'worker' : 'direct');

const makeProxy = <T extends Record<string, any>>(targetName: string, overrides: any = {}) => {
  const handler = {
    get(target: any, prop: string) {
      if (prop in target) {
        return target[prop];
      }
      return (...args: any[]) => workerClient.call(targetName, prop, args);
    }
  };
  return new Proxy(overrides, handler) as T;
};

const makeShared = <T extends Record<string, any>>(targetName: string, overrides: any = {}): T => {
  if (!useWorker) {
    throw new Error('makeShared should not be called in direct mode');
  }
  return makeProxy<T>(targetName, overrides);
};

class ChatClientProxy {
  static shared: RealChatClient = useWorker
    ? makeShared<RealChatClient>('ChatClient', {
        connectionState: () => workerClient.connectionState()
      })
    : RealChatClient.shared;
}

class MessageHandlerProxy {
  static shared: RealMessageHandler = useWorker
    ? makeShared<RealMessageHandler>('MessageHandler', {
        sendMessage: async (
          channel: any,
          text?: string,
          file?: File,
          replyOn?: any,
          mentions?: Array<any>
        ) => {
          const messageId = uuidV4();

          if (!file) {
            return workerClient.call('MessageHandler', 'sendMessageFromClient', [
              messageId,
              channel,
              text,
              file,
              replyOn,
              mentions
            ]);
          }

          if (!file.type.startsWith('video/')) {
            return workerClient.call('MessageHandler', 'sendMessageFromClient', [
              messageId,
              channel,
              text,
              file,
              replyOn,
              mentions
            ]);
          }

          try {
            const cover = await FileUtils.getVideoCover(file);
            if (cover.blob) {
              const thumbFile = new File(
                [cover.blob],
                `${FileUtils.removeExtension(file.name)}.jpg`
              );
              return workerClient.call('MessageHandler', 'sendMessageFromClient', [
                messageId,
                channel,
                text,
                file,
                replyOn,
                mentions,
                { file: thumbFile, duration: cover.duration }
              ]);
            }
          } catch {
            // fall through to worker without thumb
          }

          return workerClient.call('MessageHandler', 'sendMessageFromClient', [
            messageId,
            channel,
            text,
            file,
            replyOn,
            mentions
          ]);
        }
      })
    : RealMessageHandler.shared;
}

class AccountHandlerProxy {
  static shared: RealAccountHandler = useWorker
    ? makeShared<RealAccountHandler>('AccountHandler')
    : RealAccountHandler.shared;
}

class PushHandlerProxy {
  static shared: RealPushHandler = useWorker
    ? makeShared<RealPushHandler>('PushHandler')
    : RealPushHandler.shared;
}

class PresenceHandlerProxy {
  static shared: RealPresenceHandler = useWorker
    ? makeShared<RealPresenceHandler>('PresenceHandler')
    : RealPresenceHandler.shared;
}

class PinHandlerProxy {
  static shared: RealPinHandler = useWorker
    ? makeShared<RealPinHandler>('PinHandler')
    : RealPinHandler.shared;
}

class S3HandlerProxy {
  static shared: RealS3Handler = useWorker
    ? makeShared<RealS3Handler>('S3Handler')
    : RealS3Handler.shared;
}

class SMSHandlerProxy {
  static shared: RealSMSHandler = useWorker
    ? makeShared<RealSMSHandler>('SMSHandler')
    : RealSMSHandler.shared;
}

class CareHandlerProxy {
  static shared: RealCareHandler = useWorker
    ? makeShared<RealCareHandler>('CareHandler')
    : RealCareHandler.shared;
}

class GroupHandlerProxy {
  static shared: RealGroupHandler = useWorker
    ? makeShared<RealGroupHandler>('GroupHandler')
    : RealGroupHandler.shared;
}

class InitHandlerProxy {
  static shared: RealInitHandler = useWorker
    ? makeShared<RealInitHandler>('InitHandler')
    : RealInitHandler.shared;
}

class SuspectHandlerProxy {
  static shared: RealSuspectHandler = useWorker
    ? makeShared<RealSuspectHandler>('SuspectHandler')
    : RealSuspectHandler.shared;
}

class ChannelDBOpsProxy {
  static shared: RealChannelDBOps = useWorker
    ? makeShared<RealChannelDBOps>('ChannelDBOps', {
        getChannel: (uuid: string) => RealChannelDBOps.shared.getChannel(uuid)
      })
    : RealChannelDBOps.shared;
}

class UserDBOpsProxy {
  static shared: RealUserDBOps = useWorker
    ? makeShared<RealUserDBOps>('UserDBOps', {
        getUser: (uuid: string) => RealUserDBOps.shared.getUser(uuid)
      })
    : RealUserDBOps.shared;
}

export {
  ChatClientProxy as ChatClient,
  MessageHandlerProxy as MessageHandler,
  AccountHandlerProxy as AccountHandler,
  PushHandlerProxy as PushHandler,
  PresenceHandlerProxy as PresenceHandler,
  PinHandlerProxy as PinHandler,
  S3HandlerProxy as S3Handler,
  SMSHandlerProxy as SMSHandler,
  CareHandlerProxy as CareHandler,
  GroupHandlerProxy as GroupHandler,
  InitHandlerProxy as InitHandler,
  SuspectHandlerProxy as SuspectHandler,
  ChannelDBOpsProxy as ChannelDBOps,
  UserDBOpsProxy as UserDBOps
};
